import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const LOGO_FILE = 'Oakvale Credit Union logo1.png';
const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const LOGO_SRC = encodeURI(`${publicBase}/${LOGO_FILE}`.replace(/^\/{2,}/, '/'));

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Generate a short reference number for the receipt
function getReceiptReference(transaction) {
  const id = (transaction.id || 'mock').toString().replace(/\D/g, '');
  const date = transaction.date instanceof Date
    ? transaction.date.getTime()
    : new Date(transaction.date).getTime();
  const str = `${id}-${date}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + c;
    hash = hash & hash;
  }
  const ref = String(Math.abs(hash)).padStart(10, '0').slice(-10);
  return `NS-${ref}`;
}

// Simple 1D barcode-style stripes (decorative) from reference
function BarcodeStripes({ reference }) {
  const digits = reference.replace(/\D/g, '').split('').map(Number);
  const widths = digits.length ? digits : [1, 2, 3, 4, 5, 6, 7, 8, 9];
  return (
    <div className="flex items-end justify-center gap-0.5 h-8 sm:h-10">
      {widths.map((w, i) => (
        <div
          key={i}
          className="bg-gray-900 rounded-sm"
          style={{ width: Math.max(2, w * 2), height: `${18 + (i % 3) * 6}px` }}
        />
      ))}
    </div>
  );
}

export default function TransactionReceiptModal({ transaction, userData, formatCurrency, formatDate, onClose }) {
  const receiptRef = useRef(null);

  if (!transaction) return null;

  const date = transaction.date instanceof Date ? transaction.date : new Date(transaction.date);
  const reference = getReceiptReference(transaction);
  const memberName = userData?.name || userData?.personalInfo?.firstName || '';
  const memberEmail = userData?.email || '';
  const fromAccount = transaction.accountType || transaction.fromAccount || '';
  const description = transaction.description || '';
  const status = transaction.status || 'Completed';
  const amountAbs = Math.abs(Number(transaction.amount || 0));
  const amountDisplay = formatCurrency ? formatCurrency(amountAbs) : `$${amountAbs.toFixed(2)}`;
  const dateDisplay = formatDate ? formatDate(date) : date.toLocaleString();
  const qrPayload = JSON.stringify({
    ref: reference,
    amount: transaction.amount,
    type: transaction.type,
    date: date.toISOString(),
    desc: (transaction.description || '').slice(0, 50)
  });

  const handlePrint = () => {
    const origin = window.location.origin;
    const win = window.open('', '_blank');
    const logoAbsolute = `${origin}${LOGO_SRC}`;
    const safe = {
      reference: escapeHtml(reference),
      date: escapeHtml(dateDisplay),
      amount: escapeHtml(amountDisplay),
      status: escapeHtml(status),
      type: escapeHtml(transaction.type || ''),
      memberName: escapeHtml(memberName || '—'),
      memberEmail: escapeHtml(memberEmail || '—'),
      fromAccount: escapeHtml(fromAccount || '—'),
      description: escapeHtml(description || '—'),
      qrPayload: escapeHtml(qrPayload),
      logo: escapeHtml(logoAbsolute),
    };

    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Proof of Payment - ${reference}</title>
          <style>
            @page { size: Letter; margin: 0.75in; }
            * { box-sizing: border-box; }
            html, body { height: 100%; }
            body {
              margin: 0;
              color: #0f172a;
              font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans", "Helvetica Neue", sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .page {
              width: 100%;
              max-width: 8.5in;
              margin: 0 auto;
              border: 1px solid #e5e7eb;
              border-radius: 14px;
              padding: 28px;
            }
            .header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
            }
            .brand img { height: 48px; width: auto; display: block; }
            .addr { text-align: right; font-size: 11px; line-height: 1.35; color: #475569; }
            .addr strong { color: #0f172a; }
            .titleRow {
              margin-top: 18px;
              padding-top: 18px;
              border-top: 1px solid #e5e7eb;
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
            }
            .titleRow h1 {
              margin: 0;
              font-size: 18px;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .meta {
              text-align: right;
              font-size: 12px;
              color: #334155;
            }
            .meta .ref { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; font-weight: 700; }
            .amountBox {
              margin-top: 10px;
              border: 1px solid #dbeafe;
              background: #eff6ff;
              border-radius: 12px;
              padding: 12px 14px;
            }
            .amountLabel { font-size: 11px; color: #1e3a8a; text-transform: uppercase; letter-spacing: 0.12em; }
            .amountValue { margin-top: 4px; font-size: 22px; font-weight: 800; color: #0f172a; }
            .grid {
              margin-top: 18px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 14px;
            }
            .card {
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              padding: 14px;
              background: #ffffff;
            }
            .card h2 {
              margin: 0 0 10px 0;
              font-size: 12px;
              letter-spacing: 0.12em;
              text-transform: uppercase;
              color: #0f172a;
            }
            .row {
              display: flex;
              justify-content: space-between;
              gap: 10px;
              padding: 8px 0;
              border-top: 1px solid #f1f5f9;
              font-size: 13px;
            }
            .row:first-of-type { border-top: 0; padding-top: 0; }
            .label { color: #64748b; }
            .value { color: #0f172a; font-weight: 600; text-align: right; max-width: 60%; word-break: break-word; }
            .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
            .wide { grid-column: 1 / -1; }
            .notes {
              margin-top: 14px;
              font-size: 12px;
              color: #475569;
              line-height: 1.5;
            }
            .footer {
              margin-top: 18px;
              padding-top: 14px;
              border-top: 1px solid #e5e7eb;
              display: flex;
              align-items: flex-end;
              justify-content: space-between;
              gap: 16px;
            }
            .footer .disclaimer { font-size: 10.5px; color: #64748b; line-height: 1.45; }
            .qrWrap { text-align: right; }
            .qrWrap .qrLabel { font-size: 10px; color: #64748b; margin-bottom: 6px; }
            .qrCanvas { display: inline-block; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; }
            .printHint { display: none; }
            @media screen {
              body { background: #f3f4f6; padding: 18px; }
              .page { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12); background: #fff; }
              .printHint { display: block; margin-top: 10px; font-size: 12px; color: #475569; }
            }
          </style>
          <script src="https://unpkg.com/qrcodejs@1.0.0/qrcode.min.js"></script>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <div class="brand">
                <img src="${safe.logo}" alt="Oakvale Credit Union" />
              </div>
              <div class="addr">
                <strong>Oakvale Credit Union</strong><br />
                6255 Carrsville Hwy<br />
                Carrsville, VA 23315<br />
                Federally insured by NCUA
              </div>
            </div>

            <div class="titleRow">
              <div>
                <h1>Proof of Payment</h1>
                <div class="printHint">Use your browser print dialog to “Save as PDF”.</div>
              </div>
              <div class="meta">
                <div>Reference: <span class="ref mono">${safe.reference}</span></div>
                <div>Date &amp; time: <span class="mono">${safe.date}</span></div>
                <div class="amountBox">
                  <div class="amountLabel">Amount</div>
                  <div class="amountValue">${safe.amount}</div>
                </div>
              </div>
            </div>

            <div class="grid">
              <div class="card">
                <h2>Member</h2>
                <div class="row"><div class="label">Name</div><div class="value">${safe.memberName}</div></div>
                <div class="row"><div class="label">Email</div><div class="value">${safe.memberEmail}</div></div>
              </div>
              <div class="card">
                <h2>Payment details</h2>
                <div class="row"><div class="label">Status</div><div class="value">${safe.status}</div></div>
                <div class="row"><div class="label">Type</div><div class="value">${safe.type}</div></div>
                <div class="row"><div class="label">From account</div><div class="value">${safe.fromAccount}</div></div>
              </div>

              <div class="card wide">
                <h2>Description</h2>
                <div class="row"><div class="label">Narrative</div><div class="value" style="max-width: 78%">${safe.description}</div></div>
              </div>
            </div>

            <div class="notes">
              <strong>Recordkeeping:</strong> This document is provided as proof of payment for your records.
              If you believe this information is incorrect, contact Oakvale Credit Union support.
            </div>

            <div class="footer">
              <div class="disclaimer">
                This is a system-generated document. No signature is required.<br />
                Oakvale Credit Union is an equal opportunity lender. NMLS ID: 0123456.
              </div>
              <div class="qrWrap">
                <div class="qrLabel">Verification QR</div>
                <div class="qrCanvas" id="qr"></div>
              </div>
            </div>
          </div>

          <script>
            try {
              new QRCode(document.getElementById('qr'), {
                text: ${JSON.stringify(qrPayload)},
                width: 120,
                height: 120,
                correctLevel: QRCode.CorrectLevel.M
              });
            } catch (e) {}
          </script>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 animate-fadeIn" onClick={onClose}>
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] sm:max-h-[88vh] flex flex-col animate-fadeIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header with close */}
        <div className="flex items-center justify-end p-2 sm:p-3 border-b border-gray-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Receipt content - scrollable, compact */}
        <div ref={receiptRef} className="p-4 sm:p-5 pb-4 bg-[#fafaf9] overflow-y-auto flex-1 min-h-0">
          <div className="flex items-start justify-between gap-4">
            <img
              src={LOGO_SRC}
              alt="Oakvale Credit Union"
              className="h-10 w-auto max-w-[min(100%,220px)] object-contain object-left sm:h-12"
            />
            <div className="text-right text-[10px] sm:text-xs text-gray-500 leading-snug">
              <div className="font-semibold text-gray-700">Oakvale Credit Union</div>
              <div>6255 Carrsville Hwy</div>
              <div>Carrsville, VA 23315</div>
              <div className="mt-1">Federally insured by NCUA</div>
            </div>
          </div>

          <div className="mt-4 border-t border-gray-200 pt-4">
            <h1 className="text-[10px] sm:text-xs font-semibold text-gray-700 tracking-[0.2em] sm:tracking-[0.25em] uppercase">
              Proof of Payment
            </h1>
          </div>

          <div className="space-y-0.5 sm:space-y-1 mb-3 sm:mb-4 mt-3">
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 shrink-0">Reference</span>
              <span className="font-mono font-medium text-gray-900 truncate text-right">{reference}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 shrink-0">Date & time</span>
              <span className="text-gray-900 text-right">{formatDate(date)}</span>
            </div>
            {fromAccount ? (
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-gray-500 shrink-0">From account</span>
                <span className="text-gray-900 text-right">{fromAccount}</span>
              </div>
            ) : null}
            {memberName ? (
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-gray-500 shrink-0">Member</span>
                <span className="text-gray-900 text-right">{memberName}</span>
              </div>
            ) : null}
            {memberEmail ? (
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-gray-500 shrink-0">Member email</span>
                <span className="text-gray-900 text-right truncate max-w-[55%] sm:max-w-[220px]">{memberEmail}</span>
              </div>
            ) : null}
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 shrink-0">Description</span>
              <span className="text-gray-900 text-right max-w-[55%] sm:max-w-[200px] truncate" title={transaction.description}>{transaction.description}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 shrink-0">Status</span>
              <span
                className={`capitalize font-medium ${
                  String(transaction.status || '').toLowerCase() === 'pending'
                    ? 'text-amber-700'
                    : String(transaction.status || '').toLowerCase() === 'failed'
                      ? 'text-red-700'
                      : 'text-emerald-700'
                }`}
              >
                {transaction.status || 'Completed'}
              </span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 shrink-0">Type</span>
              <span className="text-gray-900 capitalize">{transaction.type}</span>
            </div>
          </div>

          {String(transaction.status || '').toLowerCase() === 'pending' ? (
            <div className="py-2.5 sm:py-3 border-y border-amber-200 my-3 sm:my-4">
              <p className="text-base sm:text-lg font-bold text-amber-700">Pending</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {formatCurrency(Math.abs(transaction.amount))} — not yet posted
              </p>
            </div>
          ) : (
            <div className={`py-2.5 sm:py-3 border-y border-gray-200 my-3 sm:my-4 text-base sm:text-lg font-bold ${
              transaction.type === 'credit' ? 'text-green-700' : 'text-gray-900'
            }`}>
              {transaction.type === 'credit' ? '+' : '−'} {formatCurrency(Math.abs(transaction.amount))}
            </div>
          )}

          <div className="qr flex flex-col items-center mt-3 sm:mt-4">
            <QRCodeSVG value={qrPayload} size={96} level="M" className="mb-2 sm:mb-3 w-20 h-20 sm:w-24 sm:h-24" />
            <div className="barcode">
              <BarcodeStripes reference={reference} />
              <p className="text-[10px] sm:text-xs text-gray-500 tracking-widest mt-1 sm:mt-2 font-mono ref">{reference}</p>
            </div>
          </div>

          <p className="text-center text-[10px] sm:text-xs text-gray-400 mt-4 sm:mt-5">
            This document is provided as proof of payment. Keep it for your records.
          </p>
        </div>

        {/* Actions - fixed at bottom, touch-friendly */}
        <div className="flex gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-100 bg-white shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-sm sm:text-base font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-primary-600 text-white text-sm sm:text-base font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors touch-manipulation"
          >
            Download proof (PDF)
          </button>
        </div>
      </div>
    </div>
  );
}
